/**
 * MatrixView — the user x project access grid. This VIEW is read-only (a
 * pure observation grid); membership is mutated from the Projects view
 * (ProjectsListView), the admin write surface under RBAC v2.1.
 *
 * Rows  = the UNION of every members[].user_id across all projects (anyone who
 *         has a membership row on at least one project in scope). User identity
 *         (name/email/org_role) is taken from the FIRST project where that user
 *         appears — identity is per-user, not per-project, so any row works.
 * Cols  = the projects themselves.
 * Cell  = a read-only badge (Owner / Collaborate / View / No access).
 *
 * Consumes project_role / org_role / name / email straight from the endpoint
 * (the field-naming law). ROLE_BADGE_CLASS is re-declared locally on purpose —
 * importing it from ProjectMembersPanel would create a page -> component cycle.
 */
import React, { useMemo } from 'react';
import { Badge } from '../../../components/ui/badge';
import { PROJECT_ROLE } from '../../../lib/auth';

const ROLE_BADGE_CLASS = {
  [PROJECT_ROLE.VIEW]: 'bg-slate-100 text-slate-700',
  [PROJECT_ROLE.COLLABORATE]: 'bg-blue-100 text-blue-700',
  [PROJECT_ROLE.PLAN]: 'bg-emerald-100 text-emerald-700',
};

// v2.1: a 'plan' cell is the OWNER only when it matches the project's owner_id;
// any other 'plan' cell is a co-planner.
const memberRoleLabel = (projectRole, isOwner) =>
  projectRole === PROJECT_ROLE.PLAN ? (isOwner ? 'Owner' : 'Planner') : projectRole;

const MatrixView = ({ projects, searchQuery = '' }) => {
  // Build the row set: one entry per distinct user_id, identity from first sighting.
  const rows = useMemo(() => {
    const byUser = new Map();
    for (const p of projects) {
      for (const m of p.members || []) {
        if (!byUser.has(m.user_id)) {
          byUser.set(m.user_id, {
            user_id: m.user_id,
            name: m.name,
            email: m.email,
            org_role: m.org_role,
          });
        }
      }
    }
    return Array.from(byUser.values()).sort((a, b) =>
      (a.name || a.email || '').localeCompare(b.name || b.email || '')
    );
  }, [projects]);

  // Fast lookup: project_id -> (user_id -> project_role).
  const cellRole = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      const inner = new Map();
      for (const m of p.members || []) inner.set(m.user_id, m.project_role);
      map.set(p.id, inner);
    }
    return map;
  }, [projects]);

  const q = searchQuery.trim().toLowerCase();
  const visibleRows = q
    ? rows.filter(
        (r) =>
          (r.name || '').toLowerCase().includes(q) ||
          (r.email || '').toLowerCase().includes(q)
      )
    : rows;

  if (projects.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-16 text-center"
        data-testid="matrix-view"
      >
        <p className="text-sm font-medium text-slate-900 mb-1">No projects</p>
        <p className="text-xs text-slate-400">
          There are no projects in scope to display.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto" data-testid="matrix-view">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="sticky left-0 z-10 bg-white px-4 py-3 text-left font-medium text-slate-600 min-w-[220px]">
              User
            </th>
            {projects.map((p) => (
              <th
                key={p.id}
                className="px-3 py-3 text-left font-medium text-slate-600 min-w-[170px]"
                data-testid={`matrix-col-${p.id}`}
              >
                <div className="truncate">{p.name}</div>
                {p.code && (
                  <div className="text-[10px] font-normal text-slate-400">
                    {p.code}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.length === 0 ? (
            <tr>
              <td
                colSpan={projects.length + 1}
                className="h-24 text-center text-slate-500"
              >
                No users found.
              </td>
            </tr>
          ) : (
            visibleRows.map((r) => (
              <tr
                key={r.user_id}
                className="border-b border-slate-100 hover:bg-slate-50"
                data-testid={`matrix-row-${r.user_id}`}
              >
                <td className="sticky left-0 z-10 bg-white px-4 py-2 align-middle">
                  <div className="font-medium text-slate-900">
                    {r.name || '—'}
                  </div>
                  <div className="text-xs text-slate-500">{r.email || '—'}</div>
                </td>
                {projects.map((p) => {
                  const current = cellRole.get(p.id)?.get(r.user_id) ?? null;
                  return (
                    <td
                      key={p.id}
                      className="px-3 py-2 align-middle"
                      data-testid={`matrix-cell-${r.user_id}-${p.id}`}
                    >
                      {current ? (
                        <Badge
                          className={`${ROLE_BADGE_CLASS[current] || ''} text-[10px] capitalize`}
                        >
                          {memberRoleLabel(current, r.user_id === p.owner_id)}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">No access</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MatrixView;
